'use client';

import Image from 'next/image';
import clsx from 'clsx';
import { useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { Tooltip } from './Tooltip';

interface Props extends ContentProps {
  disabled?: boolean;
  selected?: boolean;
  href: string;
  className?: string;
  tooltipClass?: string;
  onClick?: () => Promise<void>;
}

export function MediaCardButton({
  disabled,
  selected,
  data,
  href,
  className,
  tooltipClass,
  onClick,
  ...restProps
}: Props) {
  const { push } = useRouter();
  const [isPending, startTransition] = useTransition();
  const isDisabled = disabled || isPending;
  const isActive = selected || isPending;

  const baseClass = clsx(
    'card card-border border-base-300 relative items-center justify-between',
    className,
  );

  if (isDisabled) {
    return (
      <Tooltip tip={data.popuptext || undefined} colorMode="primary" className={tooltipClass}>
        <div className={clsx('h-full border', isActive && 'border-primary', baseClass)}>
          {isPending && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="loading loading-spinner loading-lg" />
            </div>
          )}
          <div className="rounded-box absolute flex h-full w-full cursor-default items-center justify-center bg-gray-500/50" />
          <CardContent data={data} {...restProps} />
        </div>
      </Tooltip>
    );
  }

  return (
    <button
      className={clsx('cursor-pointer border', isActive && 'border-primary', baseClass)}
      onClick={() => {
        if (isActive) return;
        startTransition(async () => {
          await onClick?.();
          push(href);
        });
      }}
    >
      <CardContent data={data} {...restProps} />
    </button>
  );
}

interface ContentProps {
  data: { name: string; picture: string; popuptext?: string; categoryName?: string };
  imageRenderSize?: { height: number; width: number };
  headerClass?: string;
  categoryNameClass?: string;
}

function CardContent({
  data,
  imageRenderSize = { width: 400, height: 400 },
  headerClass,
  categoryNameClass,
}: ContentProps) {
  return (
    <>
      <Image
        className="rounded-box h-auto w-full object-contain"
        src={data.picture}
        alt={`${data.name} preview`}
        width={imageRenderSize.width}
        height={imageRenderSize.height}
      />
      {!!data?.categoryName && (
        <span className={clsx('text-center', categoryNameClass)}>{data.categoryName}</span>
      )}
      <span className={clsx('w-fit text-center text-xl', headerClass)}>{data.name}</span>
    </>
  );
}
